// src/cockpit4/components/ContentMonetizationEngine.tsx
// ============================================
// 内容商业化引擎（媒体工作室 P2-功能10）- v2.0 详细改进计划
// 广告位智能规划 / 商单内容智能生成 / 变现效果追踪 / 商业化节奏建议 / 报价管理
// 数据来源：localStorage（xeaos_content_monetization）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Coins, FileSignature, TrendingUp, CalendarClock, Sparkles, BadgeDollarSign } from "lucide-react";

interface AdSlot {
  id: string;
  name: string;
  platform: string;
  position: string;
  estPrice: number;
  status: "可售" | "已售" | "锁定";
}

interface MonetizationItem {
  id: string;
  title: string;
  type: string;
  price: number;
  status: "已完成" | "进行中" | "洽谈中";
  revenue: number;
  date: string;
}

const INITIAL_SLOTS: AdSlot[] = [
  { id: "s1", name: "公众号-文末广告", platform: "公众号", position: "正文末尾", estPrice: 3000, status: "可售" },
  { id: "s2", name: "公众号-次条冠名", platform: "公众号", position: "次条顶部", estPrice: 1500, status: "已售" },
  { id: "s3", name: "小红书-收藏位", platform: "小红书", position: "合集帖", estPrice: 1200, status: "可售" },
  { id: "s4", name: "抖音-评论区置顶", platform: "抖音", position: "热门视频评论区", estPrice: 800, status: "锁定" },
  { id: "s5", name: "B站-片尾鸣谢", platform: "B站", position: "视频片尾", estPrice: 2000, status: "可售" },
];

const INITIAL_ITEMS: MonetizationItem[] = [
  { id: "m1", title: "某酒店集团 · 品牌定制文", type: "公众号软文", price: 8000, status: "已完成", revenue: 8000, date: "7月18日" },
  { id: "m2", title: "某SaaS 产品 · 种草合集", type: "小红书商单", price: 5000, status: "已完成", revenue: 5000, date: "7月25日" },
  { id: "m3", title: "某教育品牌 · 短视频植入", type: "抖音植入", price: 12000, status: "进行中", revenue: 0, date: "8月10日" },
  { id: "m4", title: "某咨询公司 · 深度评测", type: "知乎合作", price: 6500, status: "洽谈中", revenue: 0, date: "8月16日" },
];

const KEY = "xeaos_content_monetization";

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch { /* ignore */ }
  return fallback;
}

export default function ContentMonetizationEngine() {
  const [slots, setSlots] = useState<AdSlot[]>(() => load(KEY + "_slots", INITIAL_SLOTS));
  const [items, setItems] = useState<MonetizationItem[]>(() => load(KEY + "_items", INITIAL_ITEMS));
  const [tab, setTab] = useState<"slots" | "deals">("slots");

  const totalEst = useMemo(() => slots.reduce((a, s) => a + s.estPrice, 0), [slots]);
  const totalRevenue = useMemo(() => items.reduce((a, i) => a + i.revenue, 0), [items]);
  const pipeline = useMemo(() => items.filter((i) => i.status !== "已完成").reduce((a, i) => a + i.price, 0), [items]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Coins size={14} className="text-amber-400" />
        <h3 className="text-[12px] font-medium text-white">内容商业化引擎</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Monetization</span>
        <div className="ml-auto flex items-center rounded-md border border-white/[0.06] overflow-hidden">
          <button onClick={() => setTab("slots")} className={`px-2.5 py-1 text-[9px] font-mono uppercase transition-colors ${tab === "slots" ? "bg-amber-500/15 text-amber-300" : "text-gray-500 hover:text-white"}`}>广告位</button>
          <button onClick={() => setTab("deals")} className={`px-2.5 py-1 text-[9px] font-mono uppercase transition-colors ${tab === "deals" ? "bg-amber-500/15 text-amber-300" : "text-gray-500 hover:text-white"}`}>商单</button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="广告位估值" value={`¥${totalEst.toLocaleString()}/月`} color="#FBBF24" />
        <MetricCard label="已实现收入" value={`¥${totalRevenue.toLocaleString()}`} color="#34D399" />
        <MetricCard label="商单管道" value={`¥${pipeline.toLocaleString()}`} color="#38BDF8" />
        <MetricCard label="可售广告位" value={`${slots.filter((s) => s.status === "可售").length} 个`} color="#A78BFA" />
      </div>

      {tab === "slots" ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <BadgeDollarSign size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">广告位智能规划</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {slots.map((s) => (
              <div key={s.id} className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{s.name}</span>
                  <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${
                    s.status === "可售" ? "bg-emerald-500/15 text-emerald-400" : s.status === "已售" ? "bg-sky-500/15 text-sky-400" : "bg-gray-500/15 text-gray-400"
                  }`}>{s.status}</span>
                </div>
                <div className="text-[9px] font-mono text-gray-500 mt-1">{s.platform} · {s.position}</div>
                <div className="text-[11px] font-mono font-semibold text-amber-300 mt-1">¥{s.estPrice.toLocaleString()} / 期</div>
                {s.status === "可售" && (
                  <button
                    onClick={() => {
                      const next = slots.map((x) => (x.id === s.id ? { ...x, status: "锁定" as const } : x));
                      setSlots(next);
                      try { localStorage.setItem(KEY + "_slots", JSON.stringify(next)); } catch { /* ignore */ }
                    }}
                    className="mt-1.5 text-[9px] font-mono text-gray-400 hover:text-white"
                  >
                    锁定排期
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-[9px] font-mono text-cyan-400">
            <Sparkles size={10} className="mt-0.5 flex-shrink-0" />
            商业化节奏建议：本月已售 2 单，建议「8月10日-8月20日」开放品牌合作窗口，优先承接 SaaS 与教育行业商单
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <FileSignature size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">商单管理 · 报价与合同</span>
          </div>
          <div className="space-y-1.5">
            {items.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-white">{m.title}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">{m.type} · {m.date} · 报价 ¥{m.price.toLocaleString()}</div>
                </div>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  m.status === "已完成" ? "bg-emerald-500/15 text-emerald-400" : m.status === "进行中" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"
                }`}>{m.status}</span>
                {m.revenue > 0 && <span className="text-[10px] font-mono font-semibold text-emerald-400">¥{m.revenue.toLocaleString()}</span>}
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[9px] font-mono text-gray-500">
            <CalendarClock size={10} /> 合同模板已就绪 · 商单内容一键生成（对接媒体工作室）
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

